// ============================================================
// App.tsx — Layout principal con Keycloak + Ant Design + Router
// ============================================================
// Modifica la línea de importación de React arriba del todo:
import React, { useEffect, useState, createContext, useContext, useRef } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import Keycloak from "keycloak-js";
import { ConfigProvider, Spin, theme } from "antd";
import esES from "antd/locale/es_ES";
import dayjs from "dayjs";
import "dayjs/locale/es";

import { setKeycloakInstance } from "./api/client";
import MainLayout from "./components/layout/MainLayout";

// Pages
import HomePage from "./pages/HomePage";
import SolicitudesPage from "./pages/SolicitudesPage";
import SolicitudDetallePage from "./pages/SolicitudDetallePage";
import QuickWinsPage from "./pages/QuickWinsPage";
import ProyectosPage from "./pages/ProyectosPage";
import ProyectoDetallePage from "./pages/ProyectoDetallePage";
import OportunidadesPage from "./pages/OportunidadesPage";
import TareasPage from "./pages/TareasPage";
import ReportingPage from "./pages/ReportingPage";
import ImportacionPage from "./pages/ImportacionPage";
import AdminPage from "./pages/AdminPage";

dayjs.locale("es");

const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL,
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
};

// ── Custom Keycloak Context (Reemplaza a @react-keycloak/web) ────────────────
const KeycloakContext = createContext<{ keycloak: Keycloak | null; initialized: boolean }>({
  keycloak: null,
  initialized: false,
});

export const useKeycloak = () => useContext(KeycloakContext);

// ── AAO Ant Design Theme ──────────────────────────────────────────────────────
const aaoTheme = {
  token: {
    colorPrimary: "#1a1f5e",      // Azul corporativo TP
    colorSuccess: "#52c41a",
    colorWarning: "#faad14",
    colorError: "#ff4d4f",
    borderRadius: 6,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  algorithm: theme.defaultAlgorithm,
};

// ── Auth guard ────────────────────────────────────────────────────────────────
function AuthenticatedApp() {
  const { keycloak: kc, initialized } = useKeycloak();

  useEffect(() => {
    if (kc) setKeycloakInstance(kc);
  }, [kc]);

  if (!initialized || !kc) {
    return (
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100vh", gap: 16 }}>
        <Spin size="large" />
        <span style={{ fontFamily: "sans-serif", color: "#1a1f5e", fontWeight: 500 }}>Iniciando sesión...</span>
      </div>
    );
  }

  if (!kc.authenticated) {
    kc.login();
    return null;
  }

  return (
    <BrowserRouter>
      <MainLayout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/solicitudes" element={<SolicitudesPage />} />
          <Route path="/solicitudes/:id" element={<SolicitudDetallePage />} />
          <Route path="/quick-wins" element={<QuickWinsPage />} />
          <Route path="/proyectos" element={<ProyectosPage />} />
          <Route path="/proyectos/:id" element={<ProyectoDetallePage />} />
          <Route path="/oportunidades" element={<OportunidadesPage />} />
          <Route path="/tareas" element={<TareasPage />} />
          <Route path="/reporting" element={<ReportingPage />} />
          <Route path="/importacion" element={<ImportacionPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </MainLayout>
    </BrowserRouter>
  );
}

// ── Root App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [kcInstance, setKcInstance] = useState<Keycloak | null>(null);
  const [initialized, setInitialized] = useState(false);
  
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    const instance = new Keycloak(keycloakConfig);

    instance
      .init({
        onLoad: "login-required",
        checkLoginIframe: false,
        redirectUri: window.location.origin + "/",
      })
      .then(() => {
        setKcInstance(instance);
        setInitialized(true);
      })
      .catch((err) => {
        console.error("Error inicializando Keycloak:", err);
      });
  }, []);

  return (
    <KeycloakContext.Provider value={{ keycloak: kcInstance, initialized }}>
      <ConfigProvider locale={esES} theme={aaoTheme}>
        <AuthenticatedApp />
      </ConfigProvider>
    </KeycloakContext.Provider>
  );
}